import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, useLocation, Link } from "react-router-dom"
import { toast } from 'react-toastify'
import classnames from "classnames"
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import {months_list} from "../../Shared/List";
import { FaFileExport, FaPlus } from "react-icons/fa"
import BackButton from '../../Shared/BackButton'
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const UnPaidDetail = () => {
    const [unPaid, setUnPaid] = useState([])
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([])
    const [title, setTitle] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const gridRef = useRef();
    const navigate = useNavigate()
    const location = useLocation()


    useEffect(() => {

        if (location.state){
            const {data} = location.state

            fetchData({
                year: data.year,
                month: data.month,
                public_id: data.public_id,
                by: data.by
            })
            setTitle(`${data.name}, ${months_list[data.month - 1].name} - ${data.year} Un-Paid Customers`)

        }

    }, [])

    const fetchData = async (payload) => {
        setIsLoading(true)
        try {
            const response = await Server.get(`/un-paid/${payload.by}/?year=${payload.year}&month=${payload.month}&public_id=${payload.public_id}`)
            setUnPaid(response?.data)
            setIsLoading(false)

            const output = response?.data.reduce((data, row) => {
                data.difference += parseFloat(row.difference);
                data.previous_balance += parseFloat(row.previous_balance);
                data.debit += parseFloat(row.debit);
                data.total += parseFloat(row.total);
                return data;
            }, {difference: 0, previous_balance: 0.00, debit: 0.00, total: 0});
            setPinnedBottomRowData([
                {
                    difference: parseFloat(output.difference).toFixed(0),
                    debit: parseFloat(output.debit).toFixed(2),
                    total: parseFloat(output.total).toFixed(2),
                    previous_balance: parseFloat(output.previous_balance).toFixed(2)
                }
            ]);
        } catch (error) {
            setIsLoading(false)
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to load unpaid details')
            }
        }
    }

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: false,
            valueFormatter: function (params) {
            if (isNaN(params.node.id)) {
                return "";
            }
            return `${Number(params.node.rowIndex) + 1}`;
            },
        },
        {
            headerName: "Actions",
            pinned: 'left',
            minWidth: 80,
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                if (e.node.rowPinned) return '';
                const payload = {
                    location_id: e.data.location_pk,
                    year: e.data.year,
                    month: e.data.month,
                    contact: e.data.contact_number,
                };
                const hasAppointment = e.data.has_an_appointment === true;
                return (
                    <div className="d-flex gap-1">
                        <Link
                            className={classnames('btn btn-outline-info', {'disabled': hasAppointment})}
                            title={hasAppointment ? "Appointment already exists" : "Add payment appointment"}
                            to="/add-payment-appointment"
                            state={{ payload: payload }}
                            onClick={(ev) => { if (hasAppointment) ev.preventDefault(); }}
                        >
                            <FaPlus/>
                        </Link>
                    </div>
                );
            },
        },
        {
            headerName: "PID",
            field: "previous_id",
            sortable: true,
            filter: true,
        },
        {
            headerName: "ID",
            field: "location_id",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Remark",
            field: "remark",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Name",
            field: "full_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Contact",
            field: "contact_number",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Prev.Reading",
            field: "previous_reading",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Curr.Reading",
            field: "current_reading",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Difference",
            field: "difference",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Prev.Balance",
            field: "previous_balance",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Debit",
            field: "debit",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Total",
            field: "total",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Period",
            field: "period",
            sortable: true,
            filter: true,
            valueFormatter: (params) => params.value ? params.value : "1 Bille"
        },
        {
            headerName: "Rate",
            field: "rate_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Village",
            field: "village_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Zone",
            field: "zone_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Station",
            field: "station_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Line",
            field: "line_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "MeterID",
            field: "serial_number",
            sortable: true,
            filter: true,
        },
        {
            headerName: "LockID",
            field: "lock_number",
            sortable: true,
            filter: true,
        },
    ], []);

    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    const gridApiRef = useRef();

    const onGridReady = (params) => {
        gridApiRef.current = params.api;
    }

    const onExportClick = () => {
        gridApiRef.current?.exportDataAsCsv({ fileName: `${title}.csv` });
    }

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
          return {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            color: '#000',
          };
        }
        return {};
    };

    const onFilterChanged = (params) => {
        // Get only the filtered data
        const filteredRows = [];
        params.api.forEachNodeAfterFilter(node => filteredRows.push(node.data));

        // Reduce filtered data
        const output = filteredRows.reduce((data, row) => {
            data.difference += Number(row.difference) || 0;
            data.previous_balance += Number(row.previous_balance) || 0;
            data.debit += Number(row.debit) || 0;
            data.total += Number(row.total) || 0;
            return data;
        }, { difference: 0.00, previous_balance: 0.00, debit: 0.00, total: 0.00 });

        // Update pinned bottom row with filtered values
        setPinnedBottomRowData([
            {
                difference: output.difference.toFixed(0),
                debit: output.debit.toFixed(2),
                total: output.total.toFixed(2),
                previous_balance: output.previous_balance.toFixed(2),
            }
        ]);

    };

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
       <div className="row">
            <div className="col-md-6">
                <BackButton
                    to="/un-paid"
                    state={{ year: location.state?.data?.year, month: location.state?.data?.month, by: location.state?.data?.by }}
                    label="Back to Summary"
                />
            </div>
            <div className="col-md-6">
                <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
            </div>
        </div>
        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                    <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            suppressMenuHide={true}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            defaultColDef={defaultColDef}
                            onFilterChanged={onFilterChanged}

                            rowData={unPaid}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}

                            pagination={false}
                            onGridReady={onGridReady}

                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}


                            ref={gridRef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>
      <br/>
    </>
  )
}

export default UnPaidDetail